/**
 * Results view — everything the user sees above the input form after the
 * hero.
 *
 * Layout, top to bottom:
 *   1. Answer card — the headline break-even year and a one-sentence summary.
 *   2. Chart card — the two net-worth curves with a break-even marker.
 *   3. Four section panels (REN-12) — mortgage cost, opportunity cost,
 *      transaction cost, and the break-even conclusion. Each panel names
 *      its numbers in one sentence and carries a "show the math"
 *      progressive-disclosure with the formula, inputs, and computed
 *      answer that produced them.
 *
 * The persistent DOM (answer/chart/panel shells + each panel's whyNumber
 * mount) is built once by `renderResults()` so the disclosure state on the
 * popovers survives every keystroke. `updateResults()` rewrites only the
 * mutable text and re-renders the chart on each state change.
 */

import {
  toBreakEvenInputs,
  type ScenarioInputs,
} from "../defaults";
import { computeBreakEven, type BreakEvenResult } from "../math/breakEven";
import { getJurisdiction } from "../math/jurisdictions";
import { computeSensitivity } from "../math/sensitivity";
import { createResultsDisplayAdSlot } from "../ads/adsense";
import { renderNetWorthChart } from "./chart";
import { detectEdgeCases, type EdgeCaseCallout } from "./edgeCases";
import { formatCurrency, formatCurrencyCompact, formatOrdinalYear } from "./format";
import { buildPanels, type CurrencyFmt, type PanelSpec } from "./panels";
import { renderTornadoChart } from "./tornado";
import { createWhyNumber, type WhyNumberHandle } from "./whyNumber";

const MONTHS_PER_YEAR = 12;

interface PanelHandle {
  readonly key: PanelSpec["key"];
  readonly heading: HTMLElement;
  readonly headline: HTMLElement;
  readonly sentence: HTMLElement;
  readonly why: WhyNumberHandle;
}

export interface ResultsHandles {
  readonly root: HTMLElement;
  readonly headline: HTMLElement;
  readonly summary: HTMLElement;
  readonly chartMount: HTMLElement;
  readonly edgeCasesMount: HTMLElement;
  readonly tornadoMount: HTMLElement;
  readonly panels: readonly PanelHandle[];
}

/**
 * Build the persistent results scaffolding. Chart mounts as an empty
 * container that updateResults() will refill on each recompute. The four
 * panel shells are created once so their "show the math" open/closed
 * state persists across recomputes.
 */
export function renderResults(): ResultsHandles {
  const root = document.createElement("section");
  root.className = "results";
  root.setAttribute("aria-live", "polite");
  root.setAttribute("aria-labelledby", "results-heading");
  // Visually-hidden heading names the results region for landmark
  // navigation. Screen readers announce it; sighted users still see the
  // headline break-even year in the answer card below (REN-16).
  const heading = document.createElement("h2");
  heading.id = "results-heading";
  heading.className = "visually-hidden";
  heading.textContent = "Your rent-vs-buy result";
  root.append(heading);

  const answer = document.createElement("div");
  answer.className = "results-answer";
  answer.innerHTML = `
    <div class="answer-headline">—</div>
    <p class="answer-summary">Loading…</p>
  `;

  const chartCard = document.createElement("div");
  chartCard.className = "chart-mount";
  const chartMount = document.createElement("div");
  chartMount.className = "chart-mount-inner";
  chartCard.append(chartMount);

  const edgeCasesMount = document.createElement("div");
  edgeCasesMount.className = "results-edge-cases";

  const { card: tornadoCard, mount: tornadoMount } = renderTornadoShell();

  const panelsGrid = document.createElement("div");
  panelsGrid.className = "results-panels";
  const panelHandles: PanelHandle[] = [];
  for (const key of ["mortgage", "opportunity", "transactions", "breakEven"] as const) {
    const { node, handle } = renderPanelShell(key);
    panelsGrid.append(node);
    panelHandles.push(handle);
  }

  // REN-47: results-only Display ad slot. Mounted once at the bottom of
  // the results scaffold so the calculator input form above stays ad-free.
  // Serving is still consent-gated by the AdSense loader itself.
  const adSlot = createResultsDisplayAdSlot();

  root.append(answer, chartCard, edgeCasesMount, tornadoCard, panelsGrid, adSlot);

  return {
    root,
    headline: answer.querySelector<HTMLElement>(".answer-headline")!,
    summary: answer.querySelector<HTMLElement>(".answer-summary")!,
    chartMount,
    edgeCasesMount,
    tornadoMount,
    panels: panelHandles,
  };
}

/**
 * Sensitivity view (REN-15). Persistent shell holds a caption above the SVG
 * so the whole card doesn't blow away on recompute. Copy explains what the
 * ±% ranges mean — the tornado on its own doesn't say what a "±1%" bar is
 * *of*, so we spell it out once here.
 */
function renderTornadoShell(): { card: HTMLElement; mount: HTMLElement } {
  const card = document.createElement("section");
  card.className = "tornado-card";

  const header = document.createElement("div");
  header.className = "tornado-card-header";
  const heading = document.createElement("h3");
  heading.className = "tornado-card-heading";
  heading.textContent = "How your break-even year moves";
  header.append(heading);

  const mount = document.createElement("div");
  mount.className = "tornado-mount";

  const caption = document.createElement("p");
  caption.className = "tornado-card-caption";
  caption.textContent =
    "Each bar shows the break-even year when one assumption moves up or down by the amount shown. Everything else is held at your inputs. The dashed navy line is your current baseline.";

  card.append(header, mount, caption);
  return { card, mount };
}

/**
 * Recompute the scenario and write every dynamic surface. Called on every
 * form input and on initial mount.
 */
export function updateResults(handles: ResultsHandles, state: ScenarioInputs): void {
  const jurisdiction = getJurisdiction(state.jurisdictionId);
  const inputs = toBreakEvenInputs(state, () => jurisdiction);
  if (!inputs) {
    handles.headline.textContent = "—";
    handles.summary.textContent = "Unknown jurisdiction. Reset to defaults.";
    handles.chartMount.replaceChildren();
    handles.edgeCasesMount.replaceChildren();
    handles.tornadoMount.replaceChildren();
    for (const panel of handles.panels) {
      panel.headline.textContent = "—";
      panel.sentence.textContent = "";
    }
    return;
  }

  const currency = jurisdiction?.currency ?? "USD";
  const currencyFmt: CurrencyFmt = (value: number) => formatCurrency(value, currency);
  const currencyCompactFmt = (value: number) => formatCurrencyCompact(value, currency);
  const result = computeBreakEven(inputs);

  writeAnswer(handles, state, result, currencyFmt);
  writeChart(handles, state, result, currencyFmt, currencyCompactFmt);
  writeEdgeCases(handles, state, currencyFmt);
  writeTornado(handles, inputs, state);
  writePanels(handles, state, result, jurisdiction, currencyFmt);
}

function renderPanelShell(key: PanelSpec["key"]): {
  node: HTMLElement;
  handle: PanelHandle;
} {
  const node = document.createElement("article");
  node.className = `panel panel-${key}`;

  const header = document.createElement("div");
  header.className = "panel-header";
  const heading = document.createElement("h3");
  heading.className = "panel-heading";
  const headline = document.createElement("span");
  headline.className = "panel-headline";
  headline.textContent = "—";
  header.append(heading, headline);

  const sentence = document.createElement("p");
  sentence.className = "panel-sentence";

  const why = createWhyNumber();

  node.append(header, sentence, why.root);

  return {
    node,
    handle: { key, heading, headline, sentence, why },
  };
}

function writeAnswer(
  handles: ResultsHandles,
  state: ScenarioInputs,
  result: BreakEvenResult,
  currencyFmt: CurrencyFmt,
): void {
  if (result.breakEvenYear === null) {
    handles.headline.innerHTML = `<span class="never">Renting wins</span>`;
    handles.summary.textContent =
      `Owning does not overtake renting within your ${state.horizonYears}-year horizon at these assumptions.`;
    return;
  }
  const year = result.breakEvenYear;
  handles.headline.textContent = `Year ${year}`;
  const idx = year * MONTHS_PER_YEAR - 1;
  const point = result.cumulativeNetWorth[idx];
  const advantage = point ? Math.max(0, point.ownPath - point.rentPath) : 0;
  const advantageCopy = advantage > 0
    ? ` Owning is ahead by about ${currencyFmt(advantage)} at that point.`
    : "";
  handles.summary.textContent =
    `Buying beats renting starting the ${formatOrdinalYear(year)} year in your scenario.${advantageCopy}`;
}

function writeChart(
  handles: ResultsHandles,
  state: ScenarioInputs,
  result: BreakEvenResult,
  currencyFmt: CurrencyFmt,
  currencyCompactFmt: (value: number) => string,
): void {
  const chart = renderNetWorthChart({
    points: result.cumulativeNetWorth,
    horizonYears: state.horizonYears,
    breakEvenYear: result.breakEvenYear,
    formatCurrency: currencyFmt,
    formatCurrencyCompact: currencyCompactFmt,
  });
  handles.chartMount.replaceChildren(chart);
}

function writeEdgeCases(
  handles: ResultsHandles,
  state: ScenarioInputs,
  currencyFmt: CurrencyFmt,
): void {
  const callouts = detectEdgeCases(state, currencyFmt);
  handles.edgeCasesMount.replaceChildren(...callouts.map(renderEdgeCaseCard));
}

/**
 * Recompute the sensitivity table and replace the tornado SVG. Runs one
 * extra break-even pass per axis (four axes × 2 perturbations = 8 extra
 * passes); still well under the frame budget on a phone because each pass
 * is a single-digit-ms loop of pure math.
 */
function writeTornado(
  handles: ResultsHandles,
  inputs: NonNullable<ReturnType<typeof toBreakEvenInputs>>,
  state: ScenarioInputs,
): void {
  const rows = computeSensitivity(inputs);
  const chart = renderTornadoChart({ rows, horizonYears: state.horizonYears });
  handles.tornadoMount.replaceChildren(chart);
}

function renderEdgeCaseCard(callout: EdgeCaseCallout): HTMLElement {
  const card = document.createElement("aside");
  card.className = `edge-case edge-case-${callout.key}`;
  card.setAttribute("role", "note");
  card.dataset.edgeCase = callout.key;

  const title = document.createElement("h3");
  title.className = "edge-case-title";
  title.textContent = callout.title;

  const body = document.createElement("p");
  body.className = "edge-case-body";
  body.textContent = callout.body;

  card.append(title, body);
  return card;
}

function writePanels(
  handles: ResultsHandles,
  state: ScenarioInputs,
  result: BreakEvenResult,
  jurisdiction: ReturnType<typeof getJurisdiction>,
  currencyFmt: CurrencyFmt,
): void {
  const specs = buildPanels(state, result, jurisdiction, currencyFmt);
  for (const spec of specs) {
    const handle = handles.panels.find((p) => p.key === spec.key);
    if (!handle) continue;
    handle.heading.textContent = spec.heading;
    handle.headline.textContent = spec.headline;
    handle.sentence.textContent = spec.sentence;
    handle.why.update(spec.why);
  }
}
